import numeral from 'numeral';
import React, { useRef, useState, useCallback } from 'react';

import { Stack, TextField, IconButton, InputAdornment, CircularProgress } from '@mui/material';

import { financeApi } from 'src/api/finance/index';

import Iconify from './iconify';

export function AmountConverter({ control, setValue, watch, errors, selectedCurrency, t, currencies }) {
  const [loading, setLoading] = useState(false);
  const [rightValue, setRightValue] = useState('');
  const [leftValue, setLeftValue] = useState('');
  const [apiError, setApiError] = useState('');
  const debounceRef = useRef();

  const leftCurrency = selectedCurrency;
  const rightCurrency = 1; // USD

  // Watch form value for left input (amount)
  const formAmount = watch('amount');

  // Debounced API call
  const debouncedConvert = useCallback((isLeft, value) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setApiError('');
      try {
        const req = {
          source_currency: isLeft ? leftCurrency : rightCurrency,
          destination_currency: isLeft ? rightCurrency : leftCurrency,
          amount: value,
        };
        const res = await financeApi.convertAmount(req);
        const converted = res?.data?.converted_amount || '';
        const formatted = converted !== '' && !Number.isNaN(Number(converted)) ? numeral(converted).format('0,0.000') : '';
        if (isLeft) setRightValue(formatted);
        else {
          setLeftValue(formatted);
          setValue('amount', formatted);
        }
      } catch (err) {
        setApiError(t('Conversion failed'));
        if (isLeft) setRightValue('');
        else setLeftValue('');
      } finally {
        setLoading(false);
      }
    }, 400);
  }, [leftCurrency, rightCurrency, selectedCurrency, setValue, t]);

  // Handle input change
  const handleInputChange = useCallback(
    (e, isLeft) => {
      const { value } = e.target;
      setApiError('');
      if (isLeft) {
        setLeftValue(value);
        setValue('amount', value);
      } else {
        setRightValue(value);
      }
      if (!value || !selectedCurrency) {
        if (isLeft) setRightValue('');
        else setLeftValue('');
        return;
      }
      debouncedConvert(isLeft, value);
    },
    [debouncedConvert, selectedCurrency, setValue]
  );

  // Sync with form value
  React.useEffect(() => {
    setLeftValue(formAmount);
  }, [formAmount]);

  // Reset on currency change
  React.useEffect(() => {
    setLeftValue('');
    setRightValue('');
    setValue('amount', '');
  }, [selectedCurrency, setValue]);

  // Cleanup debounce on unmount
  React.useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
      <TextField
        type="number"
        label={t('Amount')}
        value={leftValue}
        onChange={(e) => handleInputChange(e, true)}
        error={!!errors?.amount || !!apiError}
        helperText={errors?.amount?.message || apiError}
        sx={{ flex: 1 }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {currencies.find(c => c.key === leftCurrency)?.symbol}
            </InputAdornment>
          ),
        }}
      />
      <Stack sx={{ position: 'relative' }}>
        <IconButton sx={{ mx: 1 }} disabled>
          <Iconify icon="solar:transfer-horizontal-bold" width={24} />
        </IconButton>
        {loading && <CircularProgress size={30} sx={{ position: 'absolute', top: 5, left: 12 }} />}
      </Stack>
      <TextField
        type="number"
        label='USD'
        value={rightValue}
        onChange={(e) => handleInputChange(e, false)}
        error={!!apiError}
        helperText={apiError}
        sx={{ flex: 1 }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              $
            </InputAdornment>
          ),
        }}
      />
    </Stack>
  );
} 